/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Building2, Plus, Search } from "lucide-react";
import { SelectDropdown, StatusBadge, TextInput } from "../../../../components/ui/index.js";
import { useAuthContext } from "../../../../components/providers/AuthProvider.jsx";
import { canManageAllProjects } from "../../../../lib/permissions.js";
import { CUSTOMER_STATE } from "../../../../components/catalog/catalogUtils.js";
import { ContactActions, CrmListCard, CrmListSkeleton } from "../../../../components/crm/CrmParts.jsx";
import { CLIENT_STATUSES, clienteName, optionFor } from "../../../../components/crm/crmUtils.js";
import ClienteFicha from "./ClienteFicha.jsx";
import useSalesOptions from "../../../../components/crm/useSalesOptions.js";

const STATUS_FILTERS = [{ value: "", label: "Todos" }, ...CLIENT_STATUSES];

/**
 * Admin › CRM › Clientes (C3D-46). La ficha va en `?id=<id>` y el alta en
 * `?nuevo=1`, sin salir de la sección del panel.
 */
export default function ListaClientes() {
    const seesAll = canManageAllProjects(useAuthContext());
    const [searchParams, setSearchParams] = useSearchParams();
    const openId = Number(searchParams.get("id")) || null;
    const creating = searchParams.get("nuevo") === "1";

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [assignedTo, setAssignedTo] = useState("");
    const [state, setState] = useState({ loading: true, error: false, items: [], next: null, count: 0 });
    const [page, setPage] = useState(1);
    const salesOptions = useSalesOptions(seesAll);

    const load = useCallback((pageToLoad = 1) => {
        setState((prev) => ({ ...prev, loading: true, error: false }));
        axios.get("/clients/", {
            params: { search: search || undefined, status: status || undefined, assigned_to: assignedTo || undefined, page: pageToLoad },
        })
            .then(({ data }) => {
                const results = data.results ?? data;
                setState((prev) => ({
                    loading: false,
                    error: false,
                    items: pageToLoad === 1 ? results : [...prev.items, ...results],
                    next: data.next ?? null,
                    count: data.count ?? results.length,
                }));
            })
            .catch(() => setState((prev) => ({ ...prev, loading: false, error: true })));
        setPage(pageToLoad);
    }, [search, status, assignedTo]);

    useEffect(() => {
        if (openId || creating) return undefined;
        const timer = setTimeout(() => load(1), 250);
        return () => clearTimeout(timer);
    }, [load, openId, creating]);

    const close = () => setSearchParams({});

    if (openId || creating) {
        return (
            <ClienteFicha
                key={openId || "nuevo"}
                clienteId={openId}
                onBack={close}
                onSaved={(id) => setSearchParams({ id: String(id) })}
                onDeleted={close}
            />
        );
    }

    return (
        <div className="p-2 sm:p-4 flex flex-col gap-4">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-bold">Clientes</h1>
                    <p className="text-sm text-base-content/70">
                        {state.count} {state.count === 1 ? "cliente" : "clientes"}{seesAll ? "" : " que llevas tú"}
                    </p>
                </div>
                <button type="button" className="btn btn-primary btn-sm gap-2" onClick={() => setSearchParams({ nuevo: "1" })}>
                    <Plus className="w-4 h-4" aria-hidden="true" /> Nuevo cliente
                </button>
            </header>

            <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
                <TextInput
                    leftIcon={<Search className="w-4 h-4" />}
                    placeholder="Buscar por nombre, empresa o email"
                    aria-label="Buscar clientes"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    inputSize="sm"
                    className="lg:max-w-sm"
                />
                <div role="group" aria-label="Filtrar por estado" className="flex gap-2 overflow-x-auto pb-1 lg:pb-0">
                    {STATUS_FILTERS.map((option) => (
                        <button
                            key={option.value || "all"}
                            type="button"
                            aria-pressed={status === option.value}
                            onClick={() => setStatus(option.value)}
                            className={`btn btn-sm rounded-full shrink-0 ${status === option.value ? "btn-neutral" : "btn-ghost border border-base-content/15"}`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                {seesAll && salesOptions.length > 0 && (
                    <SelectDropdown
                        options={[{ value: "", label: "Todos los comerciales" }, ...salesOptions]}
                        value={assignedTo}
                        onChange={setAssignedTo}
                        size="sm"
                        className="lg:w-56 lg:ml-auto"
                    />
                )}
            </div>

            {state.error && (
                <div role="alert" className="alert alert-error justify-between">
                    <span>No se han podido cargar los clientes.</span>
                    <button type="button" className="btn btn-sm" onClick={() => load(1)}>Reintentar</button>
                </div>
            )}

            {state.loading && state.items.length === 0 && <CrmListSkeleton />}

            {!state.loading && !state.error && state.items.length === 0 && (
                <div className="flex flex-col items-center text-center gap-3 py-16 rounded-xl border border-dashed border-base-content/20">
                    <Building2 className="w-10 h-10 text-base-content/40" aria-hidden="true" />
                    <p className="font-semibold">{search || status || assignedTo ? "Ningún cliente con estos filtros" : "Todavía no hay clientes"}</p>
                    <p className="text-sm text-base-content/70 max-w-sm">Crea uno a mano o convierte un lead desde su ficha.</p>
                </div>
            )}

            {state.items.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {state.items.map((cliente) => {
                        const statusOption = optionFor(CLIENT_STATUSES, cliente.status);
                        const bought = cliente.customer_state === "cliente";
                        const meta = [
                            cliente.assigned_to_name && `Comercial: ${cliente.assigned_to_name}`,
                            cliente.project_count > 0 && `${cliente.project_count} ${cliente.project_count === 1 ? "proyecto" : "proyectos"}`,
                        ].filter(Boolean).join(". ");
                        return (
                            <CrmListCard
                                key={cliente.id}
                                title={clienteName(cliente)}
                                subtitle={cliente.company_name ? [cliente.nombre, cliente.apellido].filter(Boolean).join(" ") || cliente.email : cliente.email}
                                imageUrl={cliente.prospect_logos?.[0]?.file}
                                onOpen={() => setSearchParams({ id: String(cliente.id) })}
                                badges={(
                                    <>
                                        {statusOption && <StatusBadge label={statusOption.label} tone={statusOption.tone} size="xs" />}
                                        {bought && <StatusBadge label="Ha comprado" tone="success" title={CUSTOMER_STATE.cliente.title} size="xs" outline />}
                                    </>
                                )}
                                meta={meta || null}
                                actions={<ContactActions phone={cliente.telefonos?.[0]?.numero} email={cliente.email} size="xs" />}
                            />
                        );
                    })}
                </div>
            )}

            {state.next && (
                <button type="button" className="btn btn-ghost btn-sm self-center" onClick={() => load(page + 1)} disabled={state.loading}>
                    {state.loading && <span className="loading loading-spinner loading-xs" aria-hidden="true" />}
                    Cargar más
                </button>
            )}
        </div>
    );
}
